/**
 * TrackChangesV2Container.js
 * Created by katrago on 11/20/2023.
 */
import React, {useState, useMemo, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useEngagementId, useUserPreferences} from '../../../../../../util/customHooks';
import {useAresUrlTrackChangesParameters} from '../../../../../../util/Ares/customHooks';
import Button from '../../../../../Common/BaseComponentWrappers/MotifButton';
import {hardwareIcKeyboardBackspace24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {labels} from '../../../../../../util/utils';
import {getTaskById, getTaskDocumentByDocumentId} from '../../../../../../selectors/Ares/selectors';
import {getTaskById as getTaskByIdApi} from '../../../../../../actions/Ares/TaskList/tasklistactions';
import LoadingIndicator from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import SelectDropdown from '../../../../../Common/SelectDropdown/SelectDropdown';
import {getSnapshotsForDocument} from '../../../../../../actions/Snapshot/snapshotsactions';
import EllipseControl from '../../../../../Common/EllipsesControl/EllipsesControl';
import EllipsesMoment from '../../../../../Common/EllipsesControl/EllipseMoment';
import HeaderList from '../../../../../Common/HeaderSectionList/Header/HeaderList';
import {clearSnapshotCompareView, getSnapshotById} from '../../../../../../actions/Snapshot/snapshotdetailsactions';
import Utility from '../../../../../../util/utilityfunctions';
import {loadStorage, saveStorage, clearItem} from '../../../../../Common/localStorage';
import styled from 'styled-components';
import {toggleFrameModal, frameEvents, useFrameService} from '@ey/canvascoreservices';
import ReactiveTabRenamer from '../../../../../Common/Modal/ReactiveTabRenamer';
import Icon from '../../../../../Common/BaseComponentWrappers/MotifIcon';
import {alertIcError24px} from '@ey/motif-react-wrapper/assets/icons/index';

export default function TrackChangesV2Container(props) {
	const {taskId, documentId} = useAresUrlTrackChangesParameters();
	const trackChangesBackUrl = loadStorage('trackChangesBackUrl');
	const engagementId = useEngagementId(),
		dispatch = useDispatch(),
		snapshots = useSelector((store) => store.snapshots),
		navigate = useNavigate(),
		[currentTaskId, setCurrentTaskId] = useState(taskId),
		[selectedSnapShot, setSelectedSnapShot] = useState(),
		[snapShotList, setSnapShotList] = useState([]),
		[isLoading, setIsLoading] = useState(false),
		[selectedTargetSnapShotId, setSelectedTargetSnapShotId] = useState(-1),
		[currentHeaderId, setCurrentHeaderId] = useState(0),
		[headerList, setHeaderList] = useState([]),
		userPreferences = useUserPreferences(),
		formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		task = useSelector((store) => getTaskById(store, currentTaskId));

	useEffect(() => {
		formatSnapshots(snapshots);
	}, [snapshots]);

	useEffect(() => {
		setHeaderList(formData?.headers);
		if (formData?.taskId) {
			setCurrentTaskId(formData.taskId);
			dispatch(getTaskByIdApi(engagementId, formData.taskId));
		} else {
			currentTaskId && dispatch(getTaskByIdApi(engagementId, currentTaskId));
		}
	}, [formData]);

	useEffect(() => {
		dispatch(getSnapshotsForDocument(engagementId, documentId));

		// This is done to clear the storage when component unmounts
		return () => {
			clearItem('selectedTrackChangesSnapshotId');
			clearItem('isRedirectedFromSummaryPage');
		};
	}, []);

	useEffect(() => {
		if (selectedTargetSnapShotId > 0) {
			getSnapshotDetails(selectedTargetSnapShotId, -1);
		}
	}, [selectedTargetSnapShotId]);

	const showHeaderList = useMemo(() => {
		return (
			<HeaderList
				headers={headerList}
				headerId={currentHeaderId}
				documentTypeId={formData?.documentTypeId}
				taskId={currentTaskId}
				source="TrackChanges"
			/>
		);
	}, [headerList, currentHeaderId, selectedTargetSnapShotId, engagementId, documentId]);

	const onTargetSnapshotChange = (option) => {
		setSelectedSnapShot(option);
		setSelectedTargetSnapShotId(option.value);
		if (option.value > 0) {
			saveStorage('selectedTrackChangesSnapshotId', option.value);
			saveStorage('isRedirectedFromSummaryPage', true);
		}
	};

	const getSnapshotDetails = (snapshotId, sourceSnapshotId) => {
		setIsLoading(true);
		dispatch(clearSnapshotCompareView(true));
		dispatch(getSnapshotById(engagementId, documentId, snapshotId, sourceSnapshotId))
			.then(() => {
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	//Drawer related
	const [toggled, setToggled] = useState(false);
	const frameService = useFrameService();
	const subscriptionRef = useRef(null);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameEvents.OPEN:
					case frameEvents.ACTIVE_FRAME:
						setToggled(true);
						break;

					case frameEvents.REMOVE:
					case frameEvents.CLEAR_FRAMES:
						setToggled(false);
						break;

					case frameEvents.DRAWER_TOGGLED:
						setToggled(args.open);
						break;
				}
			}
		});
	}, []);

	const onBackClickHandler = () => {
		saveStorage('isRedirectedFromSummaryPage', false);
		//Collapase drawer when we navigate through back button
		if (toggled) {
			toggleFrameModal({frameArgs: {event: frameEvents.TOGGLE_DRAWER}});
		}
		clearItem('trackChangesBackUrl');
		trackChangesBackUrl && navigate(trackChangesBackUrl);
	};

	const formatSnapshots = (snapshots) => {
		let snapshotForDropDown = [];
		let snapShotsFromServer = [];

		snapshotForDropDown.push({value: -1, label: labels.chooseVersionsToCompare, title: labels.chooseVersionsToCompare});
		if (snapshots && snapshots.length > 0) {
			snapShotsFromServer = Utility.formatSnapshotsName(snapshots);
		} else {
			snapshotForDropDown = [];
			snapshotForDropDown.push({
				value: -1,
				label: labels.noTrackChangesOption,
				title: labels.noTrackChangesOption
			});
		}
		if (loadStorage('selectedTrackChangesSnapshotId') > 0) {
			const selectedValue = snapShotsFromServer.find((c) => c.value === loadStorage('selectedTrackChangesSnapshotId'));
			setSelectedSnapShot(selectedValue);
			setSelectedTargetSnapShotId(selectedValue?.value);
		} else if (selectedSnapShot && selectedSnapShot.value !== -1) {
			setSelectedSnapShot(selectedSnapShot);
		}

		setSnapShotList(snapShotsFromServer);
	};

	const snapshotOptionLabel = ({label, userName, createDate}) => {
		return (
			<section className="selectText">
				{userName ? (
					<>
						<EllipseControl
							id="snapshotOptionNameLabel"
							content={userName}
							tooltip={userName}
							displayId={''}
							noOfLines={1}
						/>{' - '}
						<EllipsesMoment
							id="snapshotOptionDateLabel"
							useFormat
							userPreferences={userPreferences}
							date={createDate}
							useLocal={false}
						/>
					</>
				) : (
					<EllipseControl id="snapshotOptionLabel" content={label} tooltip={label} displayId={''} noOfLines={1} />
				)}
			</section>
		);
	};

	return (
		<ReactiveTabRenamer>
			<StyledTrackChangesV2Container className="mainResponsiveWrapper StyledTrackChangesV2Container">
				{trackChangesBackUrl && (
					<Button
						variant="text"
						className="back-link"
						onClick={onBackClickHandler}
						showIcon
						iconSource={hardwareIcKeyboardBackspace24px}
						showLabel
						buttonLabel={labels.back}
					/>
				)}
				<section className="track-changes-title-section">
					<h1 className="track-changes-title-heading">{`${labels.trackChanges}: ${
						task?.knowledgeTaskShortName ? task?.knowledgeTaskShortName : ''
					}`}</h1>
				</section>
				<section className="trackChangesHeader">
					<SelectDropdown
						className="trackChangesSelect"
						groupHeading
						id="Snapshots"
						isSearchable
						formatOptionLabel={snapshotOptionLabel}
						defaultValue={selectedSnapShot}
						options={snapShotList}
						onChange={onTargetSnapshotChange}
						value={selectedSnapShot}
						noOptionsMessage={() => labels.noTrackChangesOption}
						formLabel={labels.chooseVersionsToCompare}
					/>
				</section>
				<LoadingIndicator view={'inlineView'} show={isLoading} />
				{selectedTargetSnapShotId > 0 && headerList?.length > 0 && !isLoading ? (
					<section className="trackChangesBody">
						<StyledMessageContainer className="StyledMessageContainer">
							<span className="icon-container">
								<Icon src={alertIcError24px} />
							</span>
							<span className="text-container">{labels.trackChangesViewDefaultMessage}</span>
						</StyledMessageContainer>
						<section className={`snapshotTrackChangesBody`}>{showHeaderList}</section>
					</section>
				) : (
					<span className="trackChangesDefaultMessage">{labels.trackChangesDefaultMessage}</span>
				)}
			</StyledTrackChangesV2Container>
		</ReactiveTabRenamer>
	);
}

const StyledTrackChangesV2Container = styled.section`
	&.StyledTrackChangesV2Container {
		width: 100%;
		padding: 0 var(--px-116) 0 var(--px-126) !important;
		.back-link {
			margin-top: var(--px-33);
			span {
				color: var(--blue-600);
				font-weight: 700;
				line-height: var(--px-22);
				font-size: var(--px-14);
				svg {
					fill: var(--blue-600);
				}
			}
			&:hover,
			&:focus {
				box-shadow: none;
			}
		}
		.track-changes-title-section {
			.track-changes-title-heading {
				font-size: var(--px-32);
				color: var(--neutrals-1000);
				font-weight: 400;
				line-height: var(--px-42);
				margin: var(--px-46) 0 var(--px-23) 0;
			}
		}
		.trackChangesHeader {
			width: var(--px-500);
			padding-bottom: var(--px-25);
			.trackChangesSelect {
				.react-select-container {
					.react-select__control {
						.react-select__input {
							input {
								padding-top: var(--px-12) !important;
							}
						}
						.react-select__single-value {
							.selectText {
								display: flex;
								.ellipses {
									width: auto;
									max-width: calc(100% - var(--px-100));
									&#snapshotOptionDateLabel {
										margin-left: var(--px-8);
									}
								}
							}
						}
						.react-select__indicators {
							.react-select__indicator {
								svg {
									fill: var(--neutrals-900);
								}
							}
						}
					}
					.react-select__menu {
						border-radius: 0 0 var(--px-8) var(--px-8);
						.react-select__menu-list {
							background: var(--neutrals-00white);
							margin-bottom: var(--px-4);
							.react-select__menu-notice {
								font-size: var(--px-14);
								font-weight: 300;
								line-height: var(--px-24);
								color: var(--neutrals-900);
							}
							.react-select__option {
								color: var(--neutrals-900);
								line-height: var(--px-24);
								padding: var(--px-10) var(--px-14);
								font-size: var(--px-16);
								&.react-select__option--is-selected,
								&.react-select__option--is-focused {
									color: var(--neutrals-900);
									background-color: var(--neutrals-50);
									&:hover,
									&:focus {
										color: var(--neutrals-900);
									}
								}
							}
							.selectText {
								display: flex;
								.ellipses {
									width: auto;
									div {
										font-size: var(--px-14);
										font-weight: 300;
										line-height: var(--px-24);
									}
									&#snapshotOptionDateLabel {
										margin-left: var(--px-8);
									}
								}
							}
						}
					}
				}
				&.DropdownMenuOptionsOpened {
					.react-select-label {
						top: var(--px-8);
					}
				}
				&.hideInput {
					.react-select-container {
						.react-select__control {
							.react-select__input {
								padding-top: 0;
							}
						}
					}
				}
				&.DropdownMenuOptionsClosed {
					.react-select-container {
						.react-select__control {
							.react-select__input {
								input {
									caret-color: transparent;
								}
							}
						}
					}
				}
			}
		}
		.trackChangesBody {
			padding-top: var(--px-38);
			border-top: var(--px-1) solid var(--neutrals-300);
			.snapshotTrackChangesBody {
				.StyledHeaderList {
					padding: 0;
				}
			}
		}
		.trackChangesDefaultMessage {
			display: flex;
			font-size: var(--px-16);
			color: var(--neutrals-700);
			font-weight: 400;
			line-height: var(--px-22);
			padding-top: var(--px-38);
			border-top: var(--px-1) solid var(--neutrals-300);
		}
	}
`;
const StyledMessageContainer = styled.section`
	display: flex;
	background-color: var(--neutrals-00white);
	border: var(--px-1) solid var(--neutrals-200);
	padding: var(--px-10);
	border-radius: var(--px-12) var(--px-12) var(--px-12) var(--px-12);
	width: 100%;
	margin: 0 0 var(--px-8) 0;
	.icon-container {
		display: inline-flex;
		justify-content: center;
		width: var(--px-50);
		height: var(--px-24);
	}
	.text-container {
		display: inline-flex;
		align-items: center;
		font-weight: 300;
		line-height: 120%;
		font-size: var(--px-14);
		color: var(--neutrals-900);
		width: fit-content;
	}
`;
